import type { ComponentType } from "react"
import Box from "@mui/material/Box"
import type { SvgIconProps } from "@mui/material/SvgIcon"
import { LogOut } from "lucide-react"
import { AlfredoIcon } from "@/components/icons"
import DashboardIcon from "@mui/icons-material/Dashboard"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import FavoriteIcon from "@mui/icons-material/StarsRounded"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import AssignmentIcon from "@mui/icons-material/Assignment"
import HandymanIcon from "@mui/icons-material/Handyman"
import CampaignIcon from "@mui/icons-material/Campaign"
import SellIcon from "@mui/icons-material/Sell"
import PermPhoneMsgIcon from "@mui/icons-material/PermPhoneMsg"
import MenuBookIcon from "@mui/icons-material/MenuBook"
import SettingsIcon from "@mui/icons-material/Settings"
import DescriptionIcon from "@mui/icons-material/Description"
import GroupAddIcon from "@mui/icons-material/GroupAdd"
import SpeedIcon from "@mui/icons-material/Speed"
import ToggleOnIcon from "@mui/icons-material/ToggleOn"
import ChatIcon from "@mui/icons-material/Chat"
import ApartmentIcon from "@mui/icons-material/Apartment"
import HouseIcon from "@mui/icons-material/House"
import LandscapeIcon from "@mui/icons-material/Landscape"
import BusinessIcon from "@mui/icons-material/Business"
import TravelExploreIcon from "@mui/icons-material/TravelExplore"
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment"
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"

/** Adapts lucide's `LogOut` to the `SvgIconProps`-shaped `icon` slot every other menu item uses
 * (MUI icons) - lucide icons take a `size` prop, not MUI's `sx`/`fontSize`, so this wraps it in a
 * `Box` (which does understand `sx`) for layout, sized to match the MUI icons rendered alongside
 * it in the menu (`header.tsx` passes `sx={{ mr: 1, fontSize: 20 }}`). */
function SignOutIcon({ sx }: SvgIconProps) {
  return (
    <Box component="span" sx={{ display: "inline-flex", ...sx }}>
      <LogOut size={20} strokeWidth={2.75} />
    </Box>
  )
}

/** Same adapter shape as `SignOutIcon` above - `AlfredoIcon` (public/icons/alfredo.tsx) takes
 * `size`/`className`, not MUI's `sx`/`fontSize`, so this wraps it in a `Box` for layout. */
function AlfredoMenuIcon({ sx }: SvgIconProps) {
  return (
    <Box component="span" sx={{ display: "inline-flex", ...sx }}>
      <AlfredoIcon size={20} />
    </Box>
  )
}

export type AppMenuItem = {
  id: string
  labelKey: string
  route?: string
  href?: string
  /** Every current value is a `@mui/icons-material` component, which all accept the standard
   * `SvgIconProps` (`sx`, `fontSize`, `color`, ...) - typed to that instead of a bare `ElementType`
   * so callers can pass those props (e.g. `<IconComponent sx={{ fontSize: 20 }} />`) type-safely. */
  icon: ComponentType<SvgIconProps>
  adminOnly?: boolean
  hidden?: boolean
  action?: "signout"
}

export const APP_MENU_ITEMS: AppMenuItem[] = [
  { id: "dashboard", labelKey: "sideMenu.dashboard", route: "", icon: DashboardIcon },
  { id: "search-apartments", labelKey: "sideMenu.searchApartments", route: "search/apartments", icon: ApartmentIcon },
  { id: "search-houses", labelKey: "sideMenu.searchHouses", route: "search/houses", icon: HouseIcon },
  { id: "search-terrains", labelKey: "sideMenu.searchTerrains", route: "search/terrains", icon: LandscapeIcon },
  { id: "search-buildings", labelKey: "sideMenu.searchBuildings", route: "search/buildings", icon: BusinessIcon },
  { id: "hot-deals", labelKey: "sideMenu.hotDeals", route: "hot-deals", icon: LocalFireDepartmentIcon },
  {
    id: "explorer-apartments",
    labelKey: "sideMenu.explorerApartments",
    route: "search/apartments-explorer",
    icon: TravelExploreIcon,
    hidden: true
  },
  { id: "explorer-houses", labelKey: "sideMenu.explorerHouses", route: "search/houses-explorer", icon: TravelExploreIcon, hidden: true },
  {
    id: "explorer-buildings",
    labelKey: "sideMenu.explorerBuildings",
    route: "search/buildings-explorer",
    icon: TravelExploreIcon,
    hidden: true
  },
  { id: "custom-map", labelKey: "sideMenu.customMap", route: "propertyMap", icon: AddCircleIcon },
  { id: "favorites", labelKey: "sideMenu.favorites", route: "search/favorites", icon: FavoriteIcon },
  { id: "chat", labelKey: "sideMenu.chat", route: "chat", icon: ChatIcon },
  { id: "acquisition", labelKey: "sideMenu.acquisition", route: "data?stage=acquisition", icon: ShoppingCartIcon },
  { id: "planning", labelKey: "sideMenu.planning", route: "data?stage=planning", icon: AssignmentIcon },
  { id: "rebuilding", labelKey: "sideMenu.rebuilding", route: "data?stage=rebuilding", icon: HandymanIcon },
  { id: "marketing", labelKey: "sideMenu.marketing", route: "data?stage=marketing", icon: CampaignIcon },
  { id: "sales", labelKey: "sideMenu.sales", route: "data?stage=sales", icon: SellIcon },
  { id: "post-sale", labelKey: "sideMenu.postSale", route: "data?stage=post-sale", icon: PermPhoneMsgIcon },
  { id: "user-manual", labelKey: "sideMenu.userManual", route: "manual", icon: MenuBookIcon },
  { id: "user-settings", labelKey: "sideMenu.userSettings", route: "user-settings", icon: SettingsIcon, adminOnly: false },
  { id: "hot-deals-zones", labelKey: "sideMenu.hotDealZones", route: "hot-deals/zones", icon: EditLocationAltIcon, adminOnly: true },
  { id: "settings", labelKey: "sideMenu.settings", route: "settings", icon: SettingsIcon, adminOnly: true },
  { id: "alfredo-settings", labelKey: "sideMenu.alfredoSettings", route: "admin/alfredo-settings", icon: AlfredoMenuIcon, adminOnly: true },
  { id: "docs", labelKey: "sideMenu.docs", href: "/docs", icon: DescriptionIcon, adminOnly: true },
  { id: "user-access", labelKey: "sideMenu.userAccess", route: "admin/user-access", icon: GroupAddIcon, adminOnly: true },
  { id: "job-toggles", labelKey: "sideMenu.jobToggles", route: "admin/job-toggles", icon: ToggleOnIcon, adminOnly: true },
  {
    id: "pipeline-diagnostics",
    labelKey: "sideMenu.pipelineDiagnostics",
    route: "admin/pipelineDiagnostics",
    icon: SpeedIcon,
    adminOnly: true
  },
  {
    id: "alfredo-import-manager",
    labelKey: "sideMenu.importManager",
    route: "admin/alfredo-import-manager",
    icon: CloudUploadIcon,
    adminOnly: true
  },
  // alfredo-data-explorer removed - properties are now viewed through Property Pipeline
  { id: "sign-out", labelKey: "sideMenu.signOut", icon: SignOutIcon, action: "signout" }
]

export type MenuRoleOption = { id: string; labelKey: string; adminOnly?: boolean }

export const MENU_ROLE_OPTIONS: MenuRoleOption[] = APP_MENU_ITEMS.filter((item) => item.action !== "signout").map(
  ({ id, labelKey, adminOnly }) => ({ id, labelKey, adminOnly })
)

export const ALL_MENU_ROLE_IDS = MENU_ROLE_OPTIONS.map((option) => option.id)

const MENU_ROLE_ID_LOOKUP = new Set(ALL_MENU_ROLE_IDS)

export const sanitizeRoles = (roles: unknown): string[] => {
  if (!Array.isArray(roles)) return []
  const unique = new Set<string>()
  roles.forEach((role) => {
    if (typeof role !== "string") {
      return
    }
    const trimmed = role.trim()
    if (!trimmed) {
      return
    }
    if (MENU_ROLE_ID_LOOKUP.has(trimmed)) {
      unique.add(trimmed)
    }
  })
  return ALL_MENU_ROLE_IDS.filter((id) => unique.has(id))
}
